import { useState } from 'react';
import { useLang } from '../LangContext';
import translations from '../i18n';
import content from '../content.json';
import Lightbox from './Lightbox';

const HEIGHTS = [280,360,210,320,400,250,330,290];

function placeholderSrc(i, caption) {
  const grays = ['#d0d1d2','#c8c9ca','#cccdce','#d4d5d6','#cbcccd','#d2d3d4','#c9cacc','#d1d2d3'];
  const bg = grays[i % grays.length];
  const label = caption || `Location ${i+1}`;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'><rect width='600' height='400' fill='${bg}'/><line x1='0' y1='0' x2='600' y2='400' stroke='rgba(0,0,0,0.06)' stroke-width='1'/><line x1='600' y1='0' x2='0' y2='400' stroke='rgba(0,0,0,0.06)' stroke-width='1'/><text x='300' y='195' text-anchor='middle' font-family='monospace' font-size='10' fill='rgba(0,0,0,0.2)' letter-spacing='3'>${label.toUpperCase()}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export default function Location() {
  const { lang } = useLang();
  const t = translations[lang].location;
  const [hovered, setHovered] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const photos = content.location.photos;
  const captions = translations[lang].location.photos;

  // Solo foto reali per lightbox
  const realPhotos = photos
    .map((photo, i) => ({ ...photo, caption: captions[i]?.caption || photo.caption, index: i }))
    .filter(p => p.src && p.src.trim() !== '');

  const getLightboxIndex = (i) => realPhotos.findIndex(p => p.index === i);

  return (
    <section id="location" style={{ padding: '120px 0', background: '#E3E4E5' }}>
      <div style={{ padding: '0 40px' }}>
        <h2 className="section-title reveal" style={{ color: 'var(--black)' }}>{t.title}</h2>
      </div>

      <div className="reveal" style={{ columnCount: 3, columnGap: '3px', padding: '0 3px', maxWidth: '1600px', margin: '0 auto' }}>
        {photos.map((photo, i) => {
          const hasImg = photo.src && photo.src.trim() !== '';
          const imgSrc = hasImg ? `${process.env.PUBLIC_URL}/${photo.src}` : placeholderSrc(i, captions[i]?.caption);
          const caption = captions[i]?.caption || photo.caption;
          const lbIdx = getLightboxIndex(i);

          return (
            <div key={i}
              style={{ breakInside: 'avoid', marginBottom: '3px', position: 'relative', overflow: 'hidden', cursor: hasImg ? 'pointer' : 'default' }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => hasImg && lbIdx >= 0 && setLightboxIndex(lbIdx)}
            >
              <img src={imgSrc} alt={caption} style={{
                width: '100%', display: 'block',
                height: hasImg ? 'auto' : `${HEIGHTS[i % HEIGHTS.length]}px`,
                objectFit: 'cover',
                transition: 'transform 0.7s cubic-bezier(0.16,1,0.3,1)',
                transform: hovered === i ? 'scale(1.06)' : 'scale(1)',
              }} />
              <div style={{
                position: 'absolute', inset: 0,
                background: hovered === i
                  ? 'linear-gradient(to top, rgba(229,0,60,0.92) 0%, rgba(13,13,13,0.3) 50%, transparent 100%)'
                  : 'linear-gradient(to top, rgba(0,0,0,0.2) 0%, transparent 60%)',
                transition: 'background 0.5s ease',
                display: 'flex', alignItems: 'flex-end', padding: '20px 16px',
              }}>
                {caption && (
                  <p style={{
                    fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700,
                    letterSpacing: '0.06em', color: 'var(--white)', textTransform: 'uppercase',
                    transform: hovered === i ? 'translateY(0)' : 'translateY(6px)',
                    opacity: hovered === i ? 1 : 0,
                    transition: 'all 0.4s cubic-bezier(0.16,1,0.3,1)',
                  }}>{caption}</p>
                )}
                {hasImg && hovered === i && (
                  <div style={{
                    position: 'absolute', top: '16px', right: '16px',
                    fontFamily: 'var(--font-title)', fontSize: '18px', color: 'rgba(255,255,255,0.7)',
                  }}>⊕</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="reveal" style={{ textAlign: 'center', marginTop: '64px', padding: '0 40px' }}>
        <a href={content.location.mapsUrl} target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', fontFamily: 'var(--font-title)', fontWeight: 600, fontSize: '11px', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'var(--black)', border: '1px solid rgba(0,0,0,0.2)', padding: '16px 36px', transition: 'all 0.3s ease' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.2)'; e.currentTarget.style.color = 'var(--black)'; }}
        >{t.mapsLabel}</a>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={realPhotos.map(p => ({ src: `${process.env.PUBLIC_URL}/${p.src}`, caption: p.caption }))}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((lightboxIndex - 1 + realPhotos.length) % realPhotos.length)}
          onNext={() => setLightboxIndex((lightboxIndex + 1) % realPhotos.length)}
        />
      )}
    </section>
  );
}
